// Number formatters used across the dimension charts.
//
// Extracted from dimensions.ts so chart components and the HeadlineMetric
// block can import them without pulling in the full dimension config.
// The per-dimension `formatter` in dimensions.ts uses one of these.

export type Formatter = (value: number) => string;

export const formatEur: Formatter = (value) =>
  `€${Math.round(value).toLocaleString("en-GB")}`;

/** Euros at a readable precision for large figures: billions as "€5.72 bn",
 *  millions as "€7.5 m", otherwise the full euro amount. The public-budget
 *  headline is in the billions, where exact euros are noise (#215). */
export const formatEurAbbreviated: Formatter = (value) => {
  const sign = value < 0 ? "−" : "";
  const abs = Math.abs(value);
  if (abs >= 1e9) {
    return `${sign}€${(abs / 1e9).toFixed(2)} bn`;
  }
  if (abs >= 1e6) {
    return `${sign}€${(abs / 1e6).toFixed(1)} m`;
  }
  return formatEur(value);
};

export const formatPercent: Formatter = (value) =>
  `${(value * 100).toFixed(1)}%`;

export const formatRatio: Formatter = (value) => value.toFixed(3);

/** Signed percent-change between a reform and baseline value.
 *
 *  Returns e.g. `+2.5%` or `−2.5%`. Uses the minus sign character U+2212
 *  (not a hyphen) so the typography matches the rest of the UI.
 *
 *  Returns `0%` for `reform === baseline` (or both zero); returns `n/a`
 *  for a zero baseline with a non-zero reform, since the percent change
 *  is undefined.
 */
export function formatPercentChange(reform: number, baseline: number): string {
  if (baseline === 0) {
    if (reform === 0) return "0%";
    return "n/a";
  }
  const pct = ((reform - baseline) / baseline) * 100;
  if (Math.abs(pct) < 0.05) return "0%";
  const sign = pct > 0 ? "+" : "−";
  return `${sign}${Math.abs(pct).toFixed(1)}%`;
}
